<template>
    <div
      v-if="showHiddenQuestions || questionConditions?.visibility"
        :class="{
        'opacity-06 pointer-none': presenceIsFalse ||
          !questionConditions?.visibility ||
          (productBlockedInEdition?.productId && attachmentId?.toString() !== productBlockedInEdition?.productId?.toString())
      }"
    >
      <history-value
          block
          :question="question"
          :attachment-type="props.attachmentType"
          :attachment-id="props.attachmentId"
      />
      <text-input
        v-if="question.data_type?.value === QST.INPUT.TYPE.NUMERIC"
        ref="actualInput"
        class="mt-2"
        :min-width="minFieldWidth"
        :model-value="formData"
        type="number"
        density="compact"
        single-line
        :rules="inputRules(questionConditions, question)"
        v-bind="getQuestionInformations(question, formData)"
        :return-object="true"
        @submit="(data) => {submitField(data)}"
        @form-is-invalid="() => {updateFormIsInValid(question?.id)}"
      />
      <text-area-input
        v-else-if="
          question.data_type?.value === QST.INPUT.TYPE.TEXTAREA ||
          question.data_type?.value === QST.INPUT.TYPE.TEXT
        "
        ref="actualInput"
        class="mt-2"
        :min-width="minFieldWidth"
        :model-value="formData"
        density="compact"
        rows="1"
        :rules="inputRules(questionConditions, question)"
        :return-object="true"
        @submit="(data) => {submitField(data)}"
        @form-is-invalid="() => {updateFormIsInValid(question?.id)}"
      />
      <yes-no-toggle-input
        v-else-if="question.data_type?.value === QST.INPUT.TYPE.YES_NO"
        ref="actualInput"
        :compact="true"
        :model-value="formData"
        :key="attachmentId"
        :rules="inputRules(questionConditions, question)"
        @submit="(data) => {submitField(data)}"
        @form-is-invalid="() => {updateFormIsInValid(question?.id)}"
      />
      <select-input
        v-else-if="question.data_type?.value === QST.INPUT.TYPE.DROPDOWN"
        ref="actualInput"
        :model-value="formData"
        :items="(question.value && question.value.split(',').map((item) => item.trim())) || []"
        density="compact"
        :min-width="minFieldWidth"
        :rules="inputRules(questionConditions, question)"
        :return-object="true"
        :value="question.default_value"
        @submit="(data) => {submitField(data)}"
        @form-is-invalid="() => {updateFormIsInValid(question?.id)}"
      />
      <date-picker-input
          v-else-if="question.data_type?.value === QST.INPUT.TYPE.DATE"
          ref="actualInput"
          :model-value="formData"
          :rules="inputRules(questionConditions, question)"
          :min-width="minFieldWidth"
          v-bind="getQuestionInformations(question, formData)"
          @submit="(data) => {submitField(data)}"
          @form-is-invalid="() => {updateFormIsInValid(question?.id)}"
      />
    </div>
</template>

<script setup>
import { ref, inject, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { READING, QST } from '@/constants'
import DatePickerInput from '@/components/common/DatePickerInput.vue'
import _ from 'lodash'
import { QuestionnaireConditionChecker } from '@/utils/conditions/conditionChecker/QuestionnaireConditionChecker'
import YesNoToggleInput from '@/components/common/YesNoToggleInput.vue'
import HistoryValue from './HistoryValue.vue'
import TextInput from '../../../common/TextInput.vue'
import TextAreaInput from '../../../common/TextAreaInput.vue'
import SelectInput from '../../../common/SelectInput.vue'
import {useStoreManager} from "../../../../stores/storeManager";

const { t } = useI18n()
const actualInput = ref(null)

const props = defineProps({
  questions: {
    type: Object,
    required: true
  },
  attachmentType: {
    type: String,
    required: true
  },
  attachmentId: {
    type: [Number, String],
    required: true
  },
  productBlockedInEdition: {
    required: false
  },
  question: {
    type: Object,
    required: true
  },
  formData: {
    required: false
  },
  questionConditions: {
    required: true
  },
})

const emit = defineEmits(['productHasInvalidAnswer', 'submitForm', 'update:formData'])
const storeManager = useStoreManager();
const currentConfiguration = inject('currentConfiguration')
const currentPointOfSale = inject('currentPointOfSale')
const showHiddenQuestions = inject('showHiddenQuestions')
const originalReading = inject('originalReading');
const currentReadingId = inject('currentReadingId', {});
const minFieldWidth = 144
const readingAnswers = computed(() =>
    storeManager.getFromStoreByCategory('reading', 'answer') || {}
);

const updateFormIsInValid = (qid) => {
  emit('productHasInvalidAnswer', props.attachmentId, qid)
}

const validateForm = async () => {
  let valid = true;

  if (actualInput.value) {
    const testValid = actualInput.value.validate();
    if (!testValid) valid = false;
  }
  return valid;
};

const setFocus = () => {
  actualInput.value.focus();
  actualInput.value.scrollIntoView({ behavior: 'smooth', block: 'center' });
};

defineExpose({ validateForm, setFocus });

let debounceSubmit = _.debounce(submitField, 300)

function submitField(value) {
  emit('submitForm', {
    value: value,
    questionId: props.question.id
  })
}

const inputRules = (cond, qst) => {
  return [
    (v) => cond?.mandatory ? (!!v || v === false || v === 0 || t('global.validator.mandatoryField')) : true,
    (v) => conditionCheckerRule(qst, v)
  ]
}

function conditionCheckerRule(question, value) {
    const questionnaireConditionChecker = new QuestionnaireConditionChecker()
    const context = buildContext(value)
    const questionValidityCondition = question.conditions['validity']
    const validationResult = questionnaireConditionChecker.check(questionValidityCondition, context)
    return validationResult?.result === true ||
        value === null || value === undefined || value === '' ||
        validationResult?.messages?.join(t('conditions-messages.or')
    )
}

function getQuestionInformations(question, value) {
  const questionnaireConditionChecker = new QuestionnaireConditionChecker()
  const context = buildContext(value)
  const questionValidityCondition = question.conditions['validity']
  return questionnaireConditionChecker.getInformations(questionValidityCondition, context)
}

function buildContext(value) {
  const products = currentConfiguration.value?.catalog?.product || []
  return {
    currentValue: value,
    answers: Object.values(readingAnswers.value).filter((ra) => ra.reading === currentReadingId.value),
    pos: currentPointOfSale.value,
    attachementType: props.attachmentType || READING.ATTACHMENT_TYPE.NONE,
    attachementId: props.attachmentId,
    prod:
        props.attachmentType === READING.ATTACHMENT_TYPE.PRODUCT
            ? products[props.attachmentId]
            : null,
    nowDatetime: new Date()
  }
}
const presenceIsFalse = computed(() => {
  return !originalReading.value?.data?.isPresentDate && currentConfiguration.value?.settings?.promoter_geolocation
})

</script>

<style scoped>
.screen-element-table td {
  border: 1px solid #d1d1d1;
  background-color: white;
}
</style>